import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import {
    DeepPartial,
    ICustomerSearchRequest,
    IListResponse,
    ICustomer,
    ISdkToken,
    IGetCheckResponse,
    ICreateCheckResponse,
    IReport,
    ICreateCheckPayload,
    IGetCheckPayload,
    IDVReportDetails,
    ICreateCustomer,
    ISuperAgentSearchRequest,
    UserType,
} from '@lib/interfaces';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class CustomersService {
    public environment_url: string;

    headers = new HttpHeaders({
        'x-nymos-idempotency-key': crypto.randomUUID(),
        'x-no-authorization': 'true',
    });
    public constructor(private http: HttpClient) {
        this.environment_url = environment.api_url;
    }

    public filterCustomers(payload: DeepPartial<ICustomerSearchRequest>): Observable<IListResponse<ICustomer>> {
        return this.http.post<IListResponse<ICustomer>>(`${this.environment_url}nfront/v1/proxy/users:search`, payload);
    }

    // NEOIQ-2427: "Find Super Agent" — search agents by governorate (`state`) and/or
    // `is_super_agent`, both optional so the same call also serves the "nearby" flow (no `state`,
    // is_super_agent: 'true', broad limit — distance is then computed client-side).
    public searchAgents(payload: DeepPartial<ISuperAgentSearchRequest>): Observable<IListResponse<ICustomer>> {
        return this.http.post<IListResponse<ICustomer>>(`${this.environment_url}nfront/v1/proxy/agents:search`, payload);
    }

    public fetchCustomerDetailsById(customerId: string): Observable<ICustomer> {
        return this.http.get<ICustomer>(`${this.environment_url}nfront/v1/proxy/users/${customerId}`);
    }

    public updateCustomerDetailsById(customerId: string, payload: DeepPartial<ICustomer>): Observable<ICustomer> {
        return this.http.put<ICustomer>(`${this.environment_url}nfront/v1/proxy/users/${customerId}`, payload);
    }

    public generateSDKToken(userId: string) {
        return this.http.post<ISdkToken>(`${this.environment_url}nfront/v2/proxy/identityverifications/sdkToken`, {
            user_id: userId,
        });
    }

    public fetchCustomerIdvReports(userId: string): Observable<IListResponse<IReport>> {
        return this.http.get<IListResponse<IReport>>(`${this.environment_url}nfront/v1/proxy/users/${userId}/identityverificationsdetail`);
    }

    public fetchCustomerIdvReportVerificationStatus(userId: string, reportId: string): Observable<IDVReportDetails> {
        return this.http.get<IDVReportDetails>(`${this.environment_url}nfront/v1/proxy/users/${userId}/identityverificationsdetail/${reportId}`);
    }

    public createChecks(payload: ICreateCheckPayload): Observable<ICreateCheckResponse> {
        return this.http.post<ICreateCheckResponse>(`${this.environment_url}nfront/v1/proxy/identityverifications/checks`, payload);
    }

    public getChecks(payload: IGetCheckPayload): Observable<IListResponse<IGetCheckResponse>> {
        return this.http.get<IListResponse<IGetCheckResponse>>(`${this.environment_url}nfront/v1/proxy/identityverifications/checks?user_id=${payload.user_id}&check_id=${payload.check_id}&agent_id=${payload.agent_id}`);
    }

    public changeCustomerStatus(customerId: string, payload: any): Observable<ICustomer> {
        return this.http.put<ICustomer>(`${this.environment_url}nfront/v1/proxy/users/${customerId}:changeStatusAdmin`, payload);
    }

    public createAgent(payload: DeepPartial<ICreateCustomer>, agentId: string, currencies = ['USD', 'IQD']): Observable<ICustomer> {
        return this.http.post<ICustomer>(`${this.environment_url}nfront/v1/proxy/users`, {
            user_type: UserType.AGENT,
            preferred_currencies: currencies,
            id: agentId,
            ...payload,
        });
    }

    public fetchSubAgents(parentUserId: string, searchPayload: any) {
        return this.http.get<IListResponse<ICustomer>>(`${this.environment_url}nfront/v1/proxy/users?limit=${searchPayload.limit}&parent_user_id=${parentUserId}${searchPayload.after ? `&after=${searchPayload.after}` : ''}`);
    }

    public fetchAgentDetailsByMobile(mobile: string): Observable<IListResponse<ICustomer>> {
        return this.http.post<any>(`${this.environment_url}nfront/v1/proxy/users:search`, {
            mobile,
            order_by: 'desc',
            sort_by: 'created',
            user_type: UserType.AGENT,
        });
    }
}
